import {
  GeolocateControl,
  LngLatBounds,
  Map as MapLibre,
  Marker,
  NavigationControl,
  setWorkerUrl,
} from "maplibre-gl";
// MapLibre 6 resolves its worker next to its own module, which a bundle breaks; point it at the asset.
import maplibreWorkerUrl from "maplibre-gl/dist/maplibre-gl-worker.mjs?url";
import type { FeatureCollection } from "geojson";
import "maplibre-gl/dist/maplibre-gl.css";
import "./ui/styles.css";
import { geocode, type Place } from "./sources/geocode";
import type { LatLon } from "./engine/geo";
import { elevationAlong, routeToGpx } from "./engine/gpx";
import {
  MAX_WAYPOINTS,
  trailOptionLabel,
  type PlanResult,
  type RouteResult,
} from "./engine/planner";
import {
  ROUTE_COLORS,
  ROUTE_UNSELECTED_OPACITY,
  TRAIL_LONG,
  TRAIL_PATH,
  TRAIL_STRONG,
  TRAIL_TRACK,
  type TrailStyle,
} from "./ui/colors";
import { afterDrag, cycle, detentLabel, onKey, type Detent } from "./ui/detents";
import {
  cardsKey,
  km,
  metres,
  pct,
  routeNote,
  sameRoutes,
  shapeName,
  trailLine,
  waterLine,
  lightsLine,
  waypointLine,
  offRoadLine,
  trailsCredit,
} from "./ui/format";
import { profilePath } from "./ui/profile";
import { gpxToPoints, loadRuns, mergeRuns, saveRuns, type StoredRuns } from "./ui/myruns";
import type { PlanMessage, Popularity, Trails, WorkerMessage } from "./worker/protocol";

const STYLE = "https://tiles.openfreemap.org/styles/liberty";
const STORAGE_KEY = "makeatrail:prefs:v1";
const HEAT_KEY = "makeatrail:heat:v1";
const TRAILS_KEY = "makeatrail:trails:v1";
const AVOID_KEY = "makeatrail:avoid-runs:v1";

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;

const els = {
  search: $<HTMLFormElement>("search"),
  searchInput: $<HTMLInputElement>("search-input"),
  searchResults: $<HTMLUListElement>("search-results"),
  locate: $<HTMLButtonElement>("locate"),
  modeButtons: [...document.querySelectorAll<HTMLButtonElement>("[data-mode]")],
  startLabel: $<HTMLSpanElement>("start-label"),
  endLabel: $<HTMLSpanElement>("end-label"),
  clearEnd: $<HTMLButtonElement>("clear-end"),
  waypointsBox: $<HTMLDivElement>("waypoints-box"),
  waypoints: $<HTMLOListElement>("waypoints"),
  mustPass: $<HTMLButtonElement>("must-pass"),
  mustHint: $<HTMLSpanElement>("must-hint"),
  distance: $<HTMLInputElement>("distance"),
  distanceOut: $<HTMLOutputElement>("distance-out"),
  trail: $<HTMLInputElement>("trail"),
  activityButtons: [...document.querySelectorAll<HTMLButtonElement>("[data-activity]")],
  activityHint: $<HTMLSpanElement>("activity-hint"),
  hillButtons: [...document.querySelectorAll<HTMLButtonElement>("[data-hills]")],
  go: $<HTMLButtonElement>("go"),
  again: $<HTMLButtonElement>("again"),
  status: $<HTMLParagraphElement>("status"),
  progress: $<HTMLElement>("progress"),
  results: $<HTMLOListElement>("results"),
  dataSource: $<HTMLParagraphElement>("data-source"),
  profile: $<HTMLElement>("profile"),
  profileSvg: $<HTMLElement>("profile-svg") as unknown as SVGSVGElement,
  heatRow: $<HTMLElement>("heat-row"),
  heatToggle: $<HTMLButtonElement>("heat-toggle"),
  runsCount: $<HTMLSpanElement>("runs-count"),
  runsImport: $<HTMLButtonElement>("runs-import"),
  runsFile: $<HTMLInputElement>("runs-file"),
  runsClear: $<HTMLButtonElement>("runs-clear"),
  avoidRow: $<HTMLElement>("avoid-row"),
  avoidToggle: $<HTMLButtonElement>("avoid-toggle"),
  preferRow: $<HTMLElement>("prefer-row"),
  preferToggle: $<HTMLButtonElement>("prefer-toggle"),
  trailsRow: $<HTMLElement>("trails-row"),
  trailsToggle: $<HTMLButtonElement>("trails-toggle"),
  trailsDate: $<HTMLSpanElement>("trails-date"),
  sheet: $<HTMLElement>("sheet"),
  grabber: $<HTMLButtonElement>("grabber"),
  about: $<HTMLDialogElement>("about"),
  aboutBtn: $<HTMLButtonElement>("about-btn"),
};

type Mode = "start" | "end" | "via";
type Activity = "run" | "trail";

interface Waypoint {
  p: LatLon;
  name: string;
  marker: Marker;
}

interface State {
  start: LatLon | null;
  end: LatLon | null;
  mode: Mode;
  /** Run or trail (ADR 0008). */
  activity: Activity;
  /** Points to pass through, in the order added. */
  waypoints: Waypoint[];
  /** Every route must pass the waypoints (else at least one, when possible). */
  mustPass: boolean;
  hills: number;
  seed: number;
  requestId: number;
  result: PlanResult | null;
  selected: number;
  busy: boolean;
  heatOn: boolean;
  hasHeat: boolean;
  myRuns: StoredRuns | null;
  avoidRuns: boolean;
  /** "Running trails" map layer on/off. */
  trailsOn: boolean;
  /** The layer has lines to show in this area. */
  hasTrails: boolean;
  /** "Prefer running trails" (prefs.trails). */
  preferTrails: boolean;
  /** Trail state of the latest result. */
  trailsState: Trails["state"] | null;
  /** Distance asked for by the plan on screen, metres. */
  targetM: number;
}

const state: State = {
  start: null,
  end: null,
  mode: "start",
  activity: "run",
  waypoints: [],
  mustPass: false,
  hills: 0,
  seed: 1,
  requestId: 0,
  result: null,
  selected: 0,
  busy: false,
  heatOn: true,
  hasHeat: false,
  myRuns: null,
  avoidRuns: false,
  trailsOn: true,
  hasTrails: false,
  preferTrails: true,
  trailsState: null,
  targetM: 0,
};

// ---------------------------------------------------------------------------------------------
// Preferences (per-device convenience only).

function loadPrefs(): void {
  try {
    state.heatOn = localStorage.getItem(HEAT_KEY) !== "off";
    state.trailsOn = localStorage.getItem(TRAILS_KEY) !== "off";
  } catch {
    // Storage unavailable: on by default.
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const p = JSON.parse(raw) as {
      distance?: number;
      activity?: string;
      hills?: number;
      trails?: boolean;
    };
    if (typeof p.trails === "boolean") state.preferTrails = p.trails;
    if (typeof p.distance === "number") els.distance.value = String(p.distance);
    if (p.activity === "trail" || p.activity === "run") state.activity = p.activity;
    if (typeof p.hills === "number") state.hills = p.hills;
  } catch {
    // Storage unavailable: defaults are fine.
  }
}

function savePrefs(): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        distance: Number(els.distance.value),
        activity: state.activity,
        hills: state.hills,
        trails: state.preferTrails,
      }),
    );
  } catch {
    // Ignore.
  }
}

// ---------------------------------------------------------------------------------------------
// Map.

setWorkerUrl(maplibreWorkerUrl);

const map = new MapLibre({
  container: "map",
  style: STYLE,
  center: [10, 30],
  zoom: 1.6,
  attributionControl: { compact: true },
});
// Test hook for e2e (layer inspection).
(window as unknown as { __map: unknown }).__map = map;
map.addControl(new NavigationControl({ showCompass: false }), "top-right");
const geolocate = new GeolocateControl({
  positionOptions: { enableHighAccuracy: true },
  trackUserLocation: false,
});
map.addControl(geolocate, "top-right");

const pin = (cls: string) => {
  const el = document.createElement("div");
  el.className = `pin ${cls}`;
  return el;
};
const startMarker = new Marker({ element: pin("pin-start"), draggable: true });
const endMarker = new Marker({ element: pin("pin-end"), draggable: true });
startMarker.on("dragend", () => {
  const p = startMarker.getLngLat();
  setPoint("start", { lat: p.lat, lon: p.lng });
});
endMarker.on("dragend", () => {
  const p = endMarker.getLngLat();
  setPoint("end", { lat: p.lat, lon: p.lng });
});

map.on("load", () => {
  map.addSource("heat", { type: "geojson", data: emptyFC() });
  // Below the route layers; no click handlers are bound to it, so taps fall through to the map.
  map.addLayer({
    id: "heat",
    type: "line",
    source: "heat",
    layout: {
      "line-join": "round",
      "line-cap": "round",
      "line-sort-key": ["get", "pop"],
      visibility: state.heatOn ? "visible" : "none",
    },
    paint: {
      // Amber to red to magenta: stays visible on both light and dark basemaps.
      "line-color": [
        "interpolate",
        ["linear"],
        ["get", "pop"],
        0.15,
        "#ffd60a",
        0.5,
        "#ff6b00",
        0.8,
        "#e5173f",
        1,
        "#b5179e",
      ],
      "line-width": ["interpolate", ["linear"], ["zoom"], 10, 0.8, 13, 2, 16, 5, 19, 9],
      "line-opacity": 0.6,
    },
  });
  if (pendingHeat) setHeatData(pendingHeat);
  syncHeat();
  // Running trails: above the heat, below the routes. Kinds 5-7 are dashed (line-dasharray cannot
  // be data-driven), so there is one layer per style, weakest at the bottom.
  map.addSource("trails", { type: "geojson", data: emptyFC() });
  const vis = state.trailsOn ? "visible" : "none";
  const trailLayer = (id: string, style: TrailStyle, filter: unknown[]) =>
    map.addLayer({
      id,
      type: "line",
      source: "trails",
      filter: filter as never,
      layout: { "line-join": "round", "line-cap": "round", visibility: vis },
      paint: {
        "line-color": style.color,
        "line-width": [
          "interpolate",
          ["linear"],
          ["zoom"],
          10,
          style.width * 0.4,
          14,
          style.width,
          18,
          style.width * 2,
        ],
        "line-opacity": 0.9,
        ...(style.dash ? { "line-dasharray": style.dash } : {}),
      },
    });
  trailLayer("trails-long", TRAIL_LONG, ["==", ["get", "kind"], 7]);
  trailLayer("trails-path", TRAIL_PATH, ["match", ["get", "kind"], [5, 6], true, false]);
  trailLayer("trails-track", TRAIL_TRACK, ["==", ["get", "kind"], 4]);
  trailLayer("trails", TRAIL_STRONG, ["match", ["get", "kind"], [1, 2, 3], true, false]);
  if (pendingTrails) setTrailsData(pendingTrails);
  syncTrails();
  map.addSource("routes", { type: "geojson", data: emptyFC() });
  map.addLayer({
    id: "routes-casing",
    type: "line",
    source: "routes",
    layout: { "line-join": "round", "line-cap": "round" },
    paint: {
      "line-color": "#ffffff",
      "line-width": ["case", ["get", "selected"], 9, 6],
      "line-opacity": 0.9,
    },
  });
  map.addLayer({
    id: "routes-line",
    type: "line",
    source: "routes",
    layout: {
      "line-join": "round",
      "line-cap": "round",
      "line-sort-key": ["case", ["get", "selected"], 1, 0],
    },
    paint: {
      "line-color": ["get", "color"],
      "line-width": ["case", ["get", "selected"], 5, 3],
      "line-opacity": ["case", ["get", "selected"], 1, ROUTE_UNSELECTED_OPACITY],
    },
  });
  map.on("click", "routes-line", (e) => {
    const i = e.features?.[0]?.properties?.["index"];
    if (typeof i === "number") select(i);
  });
});

map.on("click", (e) => {
  if (
    map.queryRenderedFeatures(e.point, {
      layers: map.getLayer("routes-line") ? ["routes-line"] : [],
    }).length
  ) {
    return;
  }
  setPoint(state.mode, { lat: e.lngLat.lat, lon: e.lngLat.lng });
});

geolocate.on("geolocate", (ev) => {
  setPoint("start", { lat: ev.coords.latitude, lon: ev.coords.longitude });
});

let pendingHeat: FeatureCollection | null = null;

function setHeatData(fc: FeatureCollection): void {
  const src = map.getSource("heat");
  if (!src || !("setData" in src)) {
    pendingHeat = fc; // style not loaded yet
    return;
  }
  pendingHeat = null;
  (src as { setData(d: FeatureCollection): void }).setData(fc);
}

function syncHeat(): void {
  els.heatRow.hidden = !state.hasHeat;
  els.heatToggle.setAttribute("aria-checked", String(state.heatOn));
  if (!map.isStyleLoaded() && !map.getLayer("heat")) return; // applied again on load
  if (map.getLayer("heat"))
    map.setLayoutProperty("heat", "visibility", state.heatOn && state.hasHeat ? "visible" : "none");
}

let pendingTrails: FeatureCollection | null = null;
const TRAIL_LAYERS = ["trails-long", "trails-path", "trails-track", "trails"];

function setTrailsData(fc: FeatureCollection): void {
  const src = map.getSource("trails");
  if (!src || !("setData" in src)) {
    pendingTrails = fc; // style not loaded yet
    return;
  }
  pendingTrails = null;
  (src as { setData(d: FeatureCollection): void }).setData(fc);
}

function syncTrails(): void {
  els.trailsRow.hidden = !state.hasTrails;
  els.trailsToggle.setAttribute("aria-checked", String(state.trailsOn));
  // Nothing to prefer where the area has no trails.
  // Trail mode always prefers trails, so the switch only shows in run mode.
  els.preferRow.hidden = state.trailsState === "none" || state.activity === "trail";
  els.preferToggle.setAttribute("aria-checked", String(state.preferTrails));
  for (const id of TRAIL_LAYERS)
    if (map.getLayer(id))
      map.setLayoutProperty(
        id,
        "visibility",
        state.trailsOn && state.hasTrails ? "visible" : "none",
      );
}

function emptyFC(): FeatureCollection {
  return { type: "FeatureCollection", features: [] };
}

function drawRoutes(): void {
  const src = map.getSource("routes");
  if (!src || !("setData" in src)) return;
  const routes = state.result?.routes ?? [];
  const fc: FeatureCollection = {
    type: "FeatureCollection",
    features: routes.map((r, i) => ({
      type: "Feature",
      properties: {
        index: i,
        color: ROUTE_COLORS[i % ROUTE_COLORS.length],
        selected: i === state.selected,
      },
      geometry: { type: "LineString", coordinates: r.coords.map(([lat, lon]) => [lon, lat]) },
    })),
  };
  (src as { setData(d: FeatureCollection): void }).setData(fc);
}

function fitTo(routes: RouteResult[]): void {
  const b = new LngLatBounds();
  for (const r of routes) for (const [lat, lon] of r.coords) b.extend([lon, lat]);
  if (!b.isEmpty()) {
    const wide = window.matchMedia("(min-width: 760px)").matches;
    map.fitBounds(b, {
      padding: wide
        ? { top: 40, bottom: 40, left: 420, right: 60 }
        : { top: 40, bottom: Math.round(els.sheet.offsetHeight) + 20, left: 30, right: 30 },
      maxZoom: 16,
      duration: 600,
    });
  }
}

// ---------------------------------------------------------------------------------------------
// Controls.

function setMode(mode: Mode): void {
  state.mode = mode;
  for (const b of els.modeButtons)
    b.setAttribute("aria-checked", String(b.dataset["mode"] === mode));
}

function label(p: LatLon): string {
  return `${p.lat.toFixed(4)}, ${p.lon.toFixed(4)}`;
}

function setPoint(which: Mode, p: LatLon, name?: string): void {
  if (which === "via") {
    addWaypoint(p, name);
    return;
  }
  if (which === "start") {
    state.start = p;
    startMarker.setLngLat([p.lon, p.lat]).addTo(map);
    els.startLabel.textContent = `Start · ${name ?? label(p)}`;
    // Next tap sets the end, which is optional.
    setMode("end");
  } else {
    state.end = p;
    endMarker.setLngLat([p.lon, p.lat]).addTo(map);
    els.endLabel.textContent = `End · ${name ?? label(p)}`;
    els.clearEnd.hidden = false;
  }
  els.go.disabled = !state.start || state.busy;
  setStatus(
    state.start ? "Ready. Pick a distance and generate." : "Tap the map to set a start point.",
  );
}

// ---- Waypoints --------------------------------------------------------------------------------

function addWaypoint(p: LatLon, name?: string): void {
  if (state.waypoints.length >= MAX_WAYPOINTS) {
    setStatus(`At most ${MAX_WAYPOINTS} waypoints.`, true);
    return;
  }
  const el = pin("pin-via");
  const marker = new Marker({ element: el, draggable: true }).setLngLat([p.lon, p.lat]).addTo(map);
  const w: Waypoint = { p, name: name ?? label(p), marker };
  marker.on("dragend", () => {
    const q = marker.getLngLat();
    w.p = { lat: q.lat, lon: q.lng };
    w.name = label(w.p);
    renderWaypoints();
  });
  state.waypoints.push(w);
  renderWaypoints();
  setStatus(
    state.start
      ? "Waypoint added. Tap to add another, or generate."
      : "Waypoint added. Now set a start point.",
  );
}

function removeWaypoint(i: number): void {
  const [w] = state.waypoints.splice(i, 1);
  w?.marker.remove();
  renderWaypoints();
  replanIfShowing();
}

function renderWaypoints(): void {
  const items = state.waypoints.map((w, i) => {
    (w.marker.getElement() as HTMLElement).textContent = String(i + 1);
    const li = document.createElement("li");
    const num = document.createElement("span");
    num.className = "num";
    num.setAttribute("aria-hidden", "true");
    num.textContent = String(i + 1);
    const name = document.createElement("span");
    name.className = "name";
    name.textContent = `Waypoint ${i + 1} · ${w.name}`;
    const rm = document.createElement("button");
    rm.type = "button";
    rm.className = "remove";
    rm.textContent = "Remove";
    rm.setAttribute("aria-label", `Remove waypoint ${i + 1}`);
    rm.addEventListener("click", () => removeWaypoint(i));
    li.append(num, name, rm);
    return li;
  });
  els.waypoints.replaceChildren(...items);
  els.waypointsBox.hidden = state.waypoints.length === 0;
}

els.mustPass.addEventListener("click", () => {
  state.mustPass = !state.mustPass;
  els.mustPass.setAttribute("aria-checked", String(state.mustPass));
  els.mustHint.textContent = state.mustPass
    ? "On: every route passes every waypoint."
    : "Off: at least one route passes them, when possible.";
  replanIfShowing();
});

function clearEnd(): void {
  state.end = null;
  endMarker.remove();
  els.endLabel.textContent = "End (optional)";
  els.clearEnd.hidden = true;
}

function setHills(v: number): void {
  state.hills = v;
  for (const b of els.hillButtons)
    b.setAttribute("aria-checked", String(Number(b.dataset["hills"]) === v));
}

function syncLabels(): void {
  els.distanceOut.textContent = `${Number(els.distance.value).toFixed(1)} km`;
}

function setStatus(text: string, error = false): void {
  els.status.textContent = text;
  els.status.classList.toggle("error", error);
}

els.modeButtons.forEach((b) =>
  b.addEventListener("click", () => setMode(b.dataset["mode"] as Mode)),
);
els.clearEnd.addEventListener("click", clearEnd);
els.hillButtons.forEach((b) =>
  b.addEventListener("click", () => {
    setHills(Number(b.dataset["hills"]));
    savePrefs();
  }),
);
els.distance.addEventListener("input", () => {
  syncLabels();
  savePrefs();
});
function setActivity(a: Activity): void {
  state.activity = a;
  for (const b of els.activityButtons)
    b.setAttribute("aria-checked", String(b.dataset["activity"] === a));
  els.activityHint.textContent =
    a === "trail"
      ? "Real trails first: dirt paths and marked trails."
      : "Paths, pavements and bike paths; marked trails a bonus.";
  syncTrails();
}
els.activityButtons.forEach((b) =>
  b.addEventListener("click", () => {
    setActivity(b.dataset["activity"] as Activity);
    savePrefs();
    replanIfShowing();
  }),
);
els.heatToggle.addEventListener("click", () => {
  state.heatOn = !state.heatOn;
  try {
    localStorage.setItem(HEAT_KEY, state.heatOn ? "on" : "off");
  } catch {
    // Ignore.
  }
  syncHeat();
});

// ---------------------------------------------------------------------------------------------
// Sheet detents (phones). Desktop shows the side panel and ignores them.

let detent: Detent = "peek";

function setDetent(d: Detent): void {
  detent = d;
  els.sheet.dataset["detent"] = d;
  els.grabber.setAttribute("aria-label", detentLabel(d));
}

els.grabber.addEventListener("click", () => {
  if (suppressClick) {
    suppressClick = false;
    return;
  }
  setDetent(cycle(detent));
});
els.grabber.addEventListener("keydown", (ev) => {
  const next = onKey(detent, ev.key);
  if (next) {
    ev.preventDefault();
    setDetent(next);
  }
});

let suppressClick = false;
let dragStart: { y: number; h: number } | null = null;
els.grabber.addEventListener("pointerdown", (ev) => {
  dragStart = { y: ev.clientY, h: els.sheet.offsetHeight };
  els.grabber.setPointerCapture(ev.pointerId);
});
els.grabber.addEventListener("pointermove", (ev) => {
  if (!dragStart) return;
  const dy = ev.clientY - dragStart.y;
  if (Math.abs(dy) < 6 && !els.sheet.classList.contains("dragging")) return;
  // Show all content while dragging, with the height following the finger.
  els.sheet.classList.add("dragging");
  els.sheet.style.height = `${Math.max(120, dragStart.h - dy)}px`;
});
const endDrag = (ev: PointerEvent) => {
  if (!dragStart) return;
  const dy = ev.clientY - dragStart.y;
  dragStart = null;
  els.sheet.classList.remove("dragging");
  els.sheet.style.height = "";
  const next = afterDrag(detent, dy);
  if (next) {
    suppressClick = true;
    setTimeout(() => (suppressClick = false), 0); // the click that follows a drag, if any
    setDetent(next);
  }
};
els.grabber.addEventListener("pointerup", endDrag);
els.grabber.addEventListener("pointercancel", endDrag);

// ---------------------------------------------------------------------------------------------
// My runs: imported GPX files, kept on this device only.

function syncRuns(): void {
  const n = state.myRuns?.runs ?? 0;
  els.runsCount.textContent =
    n === 0 ? "None imported" : `${n} ${n === 1 ? "run" : "runs"} imported`;
  els.runsClear.hidden = n === 0;
  els.avoidRow.hidden = n === 0;
  els.avoidToggle.setAttribute("aria-checked", String(state.avoidRuns));
}

/**
 * Re-plan with the new setting if routes are showing or a plan is running. A running plan is
 * superseded (its reply carries an old id and is ignored), so a change made mid-plan is not lost.
 */
function replanIfShowing(): void {
  if (state.start && (state.result || state.busy)) generate(false);
}

els.trailsToggle.addEventListener("click", () => {
  state.trailsOn = !state.trailsOn;
  try {
    localStorage.setItem(TRAILS_KEY, state.trailsOn ? "on" : "off");
  } catch {
    // Ignore.
  }
  syncTrails();
});
els.preferToggle.addEventListener("click", () => {
  state.preferTrails = !state.preferTrails;
  savePrefs();
  syncTrails();
  replanIfShowing();
});

els.runsImport.addEventListener("click", () => els.runsFile.click());
els.runsFile.addEventListener("change", async () => {
  const files = [...(els.runsFile.files ?? [])];
  els.runsFile.value = "";
  if (files.length === 0) return;
  let added: StoredRuns = { runs: 0, pts: new Float64Array(0) };
  for (const f of files) {
    try {
      const pts = gpxToPoints(await f.text());
      if (pts.length >= 4) added = mergeRuns(added, { runs: 1, pts });
    } catch {
      // Unreadable file: skipped.
    }
  }
  if (added.runs === 0) {
    setStatus("No GPS tracks found in those files.", true);
    return;
  }
  state.myRuns = state.myRuns ? mergeRuns(state.myRuns, added) : added;
  // Importing implies wanting to avoid them; the switch can turn it off.
  state.avoidRuns = true;
  try {
    localStorage.setItem(AVOID_KEY, "on");
  } catch {
    // Ignore.
  }
  syncRuns();
  void saveRuns(state.myRuns).then(replanIfShowing);
});
els.runsClear.addEventListener("click", () => {
  state.myRuns = null;
  state.avoidRuns = false;
  syncRuns();
  void saveRuns(null).then(replanIfShowing);
});
els.avoidToggle.addEventListener("click", () => {
  state.avoidRuns = !state.avoidRuns;
  try {
    localStorage.setItem(AVOID_KEY, state.avoidRuns ? "on" : "off");
  } catch {
    // Ignore.
  }
  syncRuns();
  replanIfShowing();
});
els.locate.addEventListener("click", () => geolocate.trigger());
els.aboutBtn.addEventListener("click", () => els.about.showModal());

els.search.addEventListener("submit", (ev) => {
  ev.preventDefault();
  const q = els.searchInput.value;
  setStatus("Searching…");
  geocode(q)
    .then(showPlaces)
    .catch(() => setStatus("Search failed. Check your connection and try again.", true));
});

function showPlaces(places: Place[]): void {
  els.searchResults.replaceChildren();
  if (places.length === 0) {
    // A visible empty state in the results list, not only a status line.
    const li = document.createElement("li");
    li.className = "empty";
    const title = document.createElement("strong");
    title.textContent = "No places found";
    li.append(title, " Check the spelling or try a larger nearby place.");
    els.searchResults.append(li);
    els.searchResults.hidden = false;
    setStatus("No places found. You can also tap the map to set a start point.");
    return;
  }
  for (const p of places) {
    const li = document.createElement("li");
    const b = document.createElement("button");
    b.type = "button";
    b.textContent = p.name;
    b.addEventListener("click", () => {
      els.searchResults.hidden = true;
      const short = p.name.split(",")[0] ?? p.name;
      setPoint(state.mode, { lat: p.lat, lon: p.lon }, short);
      map.flyTo({ center: [p.lon, p.lat], zoom: 14 });
    });
    li.append(b);
    els.searchResults.append(li);
  }
  els.searchResults.hidden = false;
  setStatus(`Pick a place to set the ${state.mode === "via" ? "waypoint" : state.mode}.`);
}

// ---------------------------------------------------------------------------------------------
// Worker.

const worker = new Worker(new URL("./worker/engine.worker.ts", import.meta.url), {
  type: "module",
  // The worker resolves the site's static data (trails/v1/) from this root.
  name: new URL(import.meta.env.BASE_URL, document.baseURI).href,
});

let failedRequestId = -1;
/** What the cards on screen were built from; an identical re-plan leaves them alone. */
let lastCardsKey = "";

worker.onmessage = (ev: MessageEvent<WorkerMessage>) => {
  const m = ev.data;
  if (m.id !== state.requestId) return;
  // After an error, keep it on screen: the worker may still finish the previous area's traces
  // under this id, and those routes belong to the old request.
  if (m.id === failedRequestId && m.type !== "error") return;
  if (m.type === "progress") {
    setStatus(m.message);
  } else if (m.type === "heat") {
    state.hasHeat = m.geojson.features.length > 0;
    setHeatData(m.geojson as FeatureCollection);
    syncHeat();
  } else if (m.type === "trails") {
    state.hasTrails = m.geojson.features.length > 0;
    setTrailsData(m.geojson as unknown as FeatureCollection);
    // Cleared (generated null) when the request moves to an area without trails.
    els.trailsDate.textContent = trailsCredit(m.generated);
    syncTrails();
  } else if (m.type === "error") {
    failedRequestId = m.id;
    // The worker may have released the old area already (a new area that fails to build), so
    // its routes no longer match what is loaded: do not leave them up as if valid.
    state.result = null;
    lastCardsKey = "";
    els.results.replaceChildren();
    els.profile.hidden = true;
    drawRoutes();
    finish();
    setStatus(m.message, true);
  } else {
    const firstOfRequest = state.result === null;
    const unchanged = !firstOfRequest && sameRoutes(state.result?.routes ?? [], m.result.routes);
    state.result = m.result;
    state.trailsState = m.trails.state;
    // For tests: "loading" means a late trails re-plan is still to come.
    els.results.dataset["trails"] = m.trails.state;
    if (m.trails.state === "none") state.hasTrails = false;
    syncTrails();
    if (firstOfRequest) state.selected = 0;
    state.selected = Math.min(state.selected, Math.max(0, m.result.routes.length - 1));
    const tracesHere = ["used", "sparse", "loading"].includes(m.popularity.state);
    if (!tracesHere && state.hasHeat) {
      state.hasHeat = false;
      syncHeat();
    }
    render(m.popularity);
    els.dataSource.textContent = `Map data: ${m.source === "tiles" ? "OpenFreeMap tiles" : "Overpass"}`;
    els.dataSource.hidden = false;
    // A late re-plan (traces, trails) that finds the same routes must not redraw or rebuild cards.
    if (!unchanged) drawRoutes();
    // Results need room: peek grows to medium (a size the person chose stays).
    if (firstOfRequest && detent === "peek") setDetent("medium");
    // Bring the new routes into view: scroll the sheet down to the Generate row (instant).
    if (firstOfRequest) els.sheet.scrollTop = Math.max(0, els.go.offsetTop - 8);
    if (firstOfRequest && m.result.routes.length) fitTo(m.result.routes);
    // Controls come back with the first result; a re-ranked one may follow when traces arrive.
    finish();
  }
};

function generate(newSeed: boolean): void {
  if (!state.start) return;
  if (newSeed) state.seed++;
  state.requestId++;
  state.result = null;
  lastCardsKey = "";
  state.targetM = Number(els.distance.value) * 1000;
  state.busy = true;
  els.progress.hidden = false;
  els.go.disabled = true;
  els.again.disabled = true;
  setStatus("Starting…");
  const msg: PlanMessage = {
    type: "plan",
    id: state.requestId,
    start: state.start,
    end: state.end,
    distanceM: Number(els.distance.value) * 1000,
    prefs: {
      trail: 0,
      hills: state.hills,
      trails: state.preferTrails,
      mode: state.activity,
    },
    seed: state.seed,
    myRuns: state.myRuns ? { pts: state.myRuns.pts, avoid: state.avoidRuns } : null,
    ...(state.waypoints.length > 0
      ? { waypoints: state.waypoints.map((w) => w.p), mustPass: state.mustPass }
      : {}),
  };
  worker.postMessage(msg);
}

function finish(): void {
  state.busy = false;
  els.progress.hidden = true;
  els.go.disabled = !state.start;
  els.again.disabled = false;
}

els.go.addEventListener("click", () => generate(false));
els.again.addEventListener("click", () => generate(true));

// ---------------------------------------------------------------------------------------------
// Results.

function render(pop: Popularity): void {
  const res = state.result;
  if (!res) {
    els.results.replaceChildren();
    return;
  }
  if (res.routes.length === 0) {
    setStatus(res.warning ?? "No routes found. Try another start point or distance.", true);
    els.results.replaceChildren();
    lastCardsKey = "";
    els.profile.hidden = true;
    return;
  }
  const popNote =
    pop.state === "used"
      ? "Ranked with public GPS traces."
      : pop.state === "loading"
        ? "Checking GPS traces for popular paths…"
        : pop.state === "sparse"
          ? "Few GPS traces on these routes, ranked mostly by path type and greenery."
          : "No GPS trace data here, ranked by path type and greenery.";
  setStatus([res.warning, popNote].filter(Boolean).join(" "));
  els.again.hidden = false;

  const key = cardsKey(
    res.routes,
    [pop.state, state.trailsState, res.loop, state.targetM].join("|"),
  );
  if (key === lastCardsKey && els.results.childElementCount === res.routes.length) return;
  lastCardsKey = key;
  els.results.replaceChildren();

  res.routes.forEach((r, i) => {
    const li = document.createElement("li");
    li.className = "card";
    li.classList.toggle("selected", i === state.selected);
    const pick = document.createElement("button");
    pick.type = "button";
    pick.className = "pick";
    pick.setAttribute("aria-pressed", String(i === state.selected));
    const swatch = document.createElement("span");
    swatch.className = "swatch";
    swatch.setAttribute("aria-hidden", "true");
    swatch.style.background = ROUTE_COLORS[i % ROUTE_COLORS.length] as string;
    const title = document.createElement("span");
    title.className = "title";
    // A trail option is named for its trail ("Follow X", "2 laps of X", "X + extra loop").
    title.textContent = `${km(r.lengthM)} · ${
      r.trailOption ? trailOptionLabel(r.trailOption) : shapeName(r.shape, res.loop)
    }`;
    const meta = document.createElement("span");
    meta.className = "meta";
    const parts = [`↑ ${metres(r.climbM)}`, `${pct(r.green)} green`];
    // Share of the route on popular paths (the heat-overlay threshold), ADR 0005.
    if (pop.state === "used") parts.push(`${pct(r.popularShare)} popular`);
    if (r.newShare !== undefined) parts.push(`${pct(r.newShare)} new to you`);
    const onTrails = trailLine(r.trailShare, state.trailsState ?? "none");
    if (onTrails) parts.push(onTrails);
    const offRoad = state.activity === "trail" ? offRoadLine(r.naturalShare) : null;
    if (offRoad) parts.push(offRoad);
    for (const extra of [waterLine(r.water), lightsLine(r.signals)]) if (extra) parts.push(extra);
    const via = waypointLine(r.passesWaypoints);
    if (via) parts.unshift(via);
    meta.textContent = parts.join(" · ");
    pick.append(swatch, title, meta);
    const noteText = routeNote(r, state.targetM);
    if (noteText && state.trailsState === "used") {
      const note = document.createElement("span");
      note.className = "note";
      note.textContent = noteText;
      pick.append(note);
    }
    pick.addEventListener("click", () => select(i));
    const gpx = document.createElement("button");
    gpx.type = "button";
    gpx.className = "gpx";
    gpx.textContent = "GPX";
    gpx.setAttribute("aria-label", `Download route ${i + 1} as GPX`);
    gpx.addEventListener("click", () => download(r, i));
    li.append(pick, gpx);
    els.results.append(li);
  });
  renderProfile();
}

function select(i: number): void {
  state.selected = i;
  els.results.querySelectorAll(".card").forEach((c, j) => {
    c.classList.toggle("selected", j === i);
    c.querySelector(".pick")?.setAttribute("aria-pressed", String(j === i));
  });
  drawRoutes();
  renderProfile();
}

function renderProfile(): void {
  const r = state.result?.routes[state.selected];
  if (!r || r.climbM + r.descentM < 1) {
    els.profile.hidden = true;
    return;
  }
  const { line, area, min, max } = profilePath(r.profile, 320, 80);
  const color = ROUTE_COLORS[state.selected % ROUTE_COLORS.length] as string;
  els.profileSvg.innerHTML = `<path d="${area}" fill="${color}" fill-opacity="0.15"/><path d="${line}" fill="none" stroke="${color}" stroke-width="2" vector-effect="non-scaling-stroke"/>`;
  els.profileSvg.setAttribute(
    "aria-label",
    `Elevation from ${Math.round(min)} to ${Math.round(max)} metres`,
  );
  const cap = els.profile.querySelector("figcaption");
  if (cap)
    cap.textContent = `Elevation · ${Math.round(min)}–${Math.round(max)} m · ↑ ${metres(r.climbM)} ↓ ${metres(r.descentM)}`;
  els.profile.hidden = false;
}

function download(r: RouteResult, i: number): void {
  const name = `MakeATrail ${km(r.lengthM)} ${i + 1}`;
  const blob = new Blob([routeToGpx(name, r.coords, elevationAlong(r.coords, r.profile))], {
    type: "application/gpx+xml",
  });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `makeatrail-${(r.lengthM / 1000).toFixed(1)}km-${i + 1}.gpx`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

// ---------------------------------------------------------------------------------------------

loadPrefs();
try {
  state.avoidRuns = localStorage.getItem(AVOID_KEY) === "on";
} catch {
  // Off by default.
}
void loadRuns().then((r) => {
  state.myRuns = r;
  if (!r) state.avoidRuns = false;
  syncRuns();
  // A plan sent before the runs loaded went out without them: send it again (it supersedes one
  // still in flight).
  if (r && state.start && (state.busy || state.result)) generate(false);
});
syncRuns();
setDetent("peek");
setHills(state.hills);
setActivity(state.activity);
syncLabels();
syncHeat();
